import { globals } from 'config/globals';
import dev from 'config/dev';
import webglHome from 'config/webgl-home';
import { MouseController } from 'app/chrome/mouse/MouseController';

import { TimelineStateType } from 'app/views/home/timeline/timeline.types';
import type { HomeTimelineDeps } from 'app/views/home/timeline/timeline.types';
import { TimelineCSSSync } from 'app/views/home/timeline/TimelineCSSSync';
import { TimelineDOMTransitions } from 'app/views/home/timeline/TimelineDOMTransitions';

import { TimelineItem, TimelineItemType } from 'app/views/home/timeline/TimelineItem';
import { TimelineItemFactory } from 'app/views/home/timeline/TimelineItemFactory';
import { TimelineItemGroup } from 'app/views/home/timeline/itemMotion/TimelineItemGroup';
import { TimelineRegionManager } from 'app/views/home/timeline/TimelineRegionManager';
import { TimelineRegionParams } from 'app/views/home/timeline/TimelineRegion';
import { TimelineSceneColours } from 'app/views/home/timeline/TimelineSceneColours';
import { TimelineZones } from 'app/views/home/timeline/TimelineZones';

import { Core } from 'core/Core';
import { DOMHelper } from 'core/DOMHelper';

import { DepartmentType } from 'app/data/Enums';

import { HomeViewConfig } from 'app/model/HomeViewConfig';
import { Point } from 'app/contracts/Geometry';


import { particles } from 'app/contracts/Particles';

import DelayHelper from 'utils/timers';
import { device } from 'utils/device';

import ParticleHitTests from 'app/views/home/targets/ParticleHitTests';
import ParticleTargets from 'app/views/home/targets/ParticleTargets';
import { HomeStageSkeleton } from 'app/views/home/scenes/HomeStageSkeleton';
import { createHomeWebGL } from 'app/views/home/webgl/HomeWebGLFactory';
import type {
	HomeItemGeometry,
	HomeStageSlots,
	HomeWebGL,
	HomeWebGLDeps,
	HomeWebGLSizes
} from 'app/views/home/webgl/webgl.types';

export class HomeTimeline {
	public onCompleteExit: Function;

	private _config: HomeViewConfig;
	private _active: boolean = true;
	private _created: boolean = false;

	private _progress: number = 0;
	private _velocity: number = 0;
	private _mouseController: MouseController;


	private _$homeContainer: PIXI.Container;
	//The ordered top-level layer slots of $homeContainer (built by
	//HomeStageSkeleton in createScene — guides / itemsPass1 /
	//webglMain / itemsPass2, in draw order):
	private _slots: HomeStageSlots;
	private _containerX: number;

	//THE authoritative home size/layout object (HomeWebGLSizes) — stable
	//identity for the life of the timeline, updated IN PLACE on resize /
	//setVirtualWidth and handed as-is to the WebGL layer (create/resize) and
	//to TimelineZones/TimelineCSSSync via deps. Replaces the old private
	//_innerWidth/_innerHeight/_virtualWidth/_virtualHeight/_viewPortCount
	//copies. Fields start undefined exactly like the old privates did
	//(viewPortCount keeps its old `= 3` default); zones/zonePositions refs
	//are re-snapshotted from sceneProps at the former _buildWebGLSizes call
	//points (see _syncSizesSnapshot):
	private _sizes: HomeWebGLSizes = {
		innerWidth: undefined as number,
		innerHeight: undefined as number,
		virtualWidth: undefined as number,
		virtualHeight: undefined as number,
		viewPortCount: 3, //Can set to lower (as confugured in JSON)
		displayScale: 1.0,
		positionScale: undefined as number,
		zones: undefined as HomeWebGLSizes['zones'],
		zonePositions: undefined as HomeWebGLSizes['zonePositions']
	};

	//The shared item registry (the HomeItemGeometry contract's getItems /
	//itemSprites serve off it) — construction is factory-side, storage here:
	private _items: Array<TimelineItem> = [];

	//JSON -> TimelineItem construction (extracted verbatim — see TimelineItemFactory):
	private _itemFactory: TimelineItemFactory;

	private _dom: DOMHelper;
	private _deps: HomeTimelineDeps;

	private _state: TimelineStateType = TimelineStateType.INTRO;

	private _timelineRegionManager: TimelineRegionManager;

	//CSS/DOM element sync (extracted verbatim — see TimelineCSSSync):
	private _cssSync: TimelineCSSSync;

	//Item/DOM halves of the work-transition resets, plus the _domRefs storage
	//they read (extracted verbatim — see TimelineDOMTransitions):
	private _domTransitions: TimelineDOMTransitions;

	//The shared TV-frame item sprites (same sprites HomeScreens.init caches);
	//null until _initScreensElements runs — TimelineCSSSync reads its
	//--home-ui-width / --screens-tv-edge metrics off these via deps:
	private _screensTvSprites: { topLeft: PIXI.Sprite; topRight: PIXI.Sprite } = null;

	//The swap-side WebGL layer behind the facade contract ('particles' today):
	private _webgl: HomeWebGL;
	private _sceneProps: particles.SharedSceneContext; //Keep track, passed down to all required partciles/elements

	//Spaces/systems tint passes + the colourise-only sprite refs they lazily
	//gather (extracted verbatim — see TimelineSceneColours):
	private _sceneColours: TimelineSceneColours;

	//Zone / content-position math — writes sceneProps.zones / zonePositions each update()
	private _zones: TimelineZones;

	constructor(
		mouseController: MouseController,
		config: HomeViewConfig,
		deps: HomeTimelineDeps,
		webglDeps: HomeWebGLDeps
	) {
		this._dom = Core.DOM;
		this._deps = deps;
		this._config = config;
		this._mouseController = mouseController;

		this._timelineRegionManager = new TimelineRegionManager();

		//The swap-side WebGL layer (config/webgl-home homeType, 'particles'
		//today). Constructed here — not in createScene — to keep the
		//preload timing byte-identical: the implementation's constructor
		//performs the previously constructor-inline pixel-data load (which
		//fires reportPixelDataReady) and schedules the deferred icon pixel
		//push:
		this._webgl = createHomeWebGL(webglHome.homeType, webglDeps);

		this._createSceneProps();

		//JSON -> item construction (see TimelineItemFactory) — getSlots is null
		//until createScene builds the skeleton, which is before the
		//factory is ever invoked. getWebGLType reads the layer constructed
		//just above (so it is valid from here on) — the factory's
		//webglTypes item filter checks it per createItems call:
		this._itemFactory = new TimelineItemFactory(this._sceneProps, this._config, {
			getSlots: () => this._slots,
			getWebGLType: () => this._webgl.type
		});

		//Zone config creation (HomeZonesConfig.createCaches + computedHomeZoneConfig)
		//runs in the TimelineZones constructor:
		this._zones = new TimelineZones(this._sceneProps, {
			getProgress: () => this._progress,
			getContainerX: () => this._containerX,
			sizes: this._sizes
		});

		//CSS-element sync (intro para / screens / spaces / systems HTML tracking,
		//explore-label scramble, projects-click prompt) — samples frame state via deps:
		this._cssSync = new TimelineCSSSync(this._dom, {
			getProgress: () => this._progress,
			getVelocity: () => this._velocity,
			getContainerX: () => this._containerX,
			sizes: this._sizes,
			getScreensTvSprites: () => this._screensTvSprites
		});

		//Item/DOM halves of the work-transition resets (extracted verbatim —
		//see TimelineDOMTransitions); samples the item registry via deps:
		this._domTransitions = new TimelineDOMTransitions({
			getItems: () => this._items
		});

		//Spaces/systems tint passes + their colourise-only sprite refs
		//(extracted verbatim — see TimelineSceneColours); samples the item
		//registry via the id lookup:
		this._sceneColours = new TimelineSceneColours({
			getItems: (id: string) => this._getItemsById(id)
		});
	}

	/** The shared-identity scene props bag (HomeWebGLDeps.getSceneProps). */
	public get sceneProps(): particles.SharedSceneContext {
		return this._sceneProps;
	}

	public get containerX(): number {
		return this._containerX;
	}

	public get progress(): number {
		return this._progress;
	}

	public get virtualWidth(): number {
		return this._sizes.virtualWidth;
	}

	public freeze(): void {
		this._active = false;
		this._webgl.freeze();
	}

	public unfreeze(): void {
		this._active = true;
		this._webgl.unfreeze();
	}

	public mouseDown(): void {}

	public mouseUp(): void {}

	public create(): void {}

	/** Home item textures are ready so */
	public createScene($homeContainer: PIXI.Container, spritesheet: PIXI.Spritesheet): void {
		this.resize();
		this._setContainerX();
		this._$homeContainer = $homeContainer;

		this._$homeContainer.alpha = 0;

		//The ordered layer slots become the first (and only direct) children of
		//the home container — everything below mounts into a slot:
		this._slots = new HomeStageSkeleton(this._$homeContainer).slots;

		//Zone debug guides (HomeZonesConfig.showGuides) draw into the guides slot:
		this._zones.setGuideContainer(this._slots.guides);

		this._checkHasParticlesCreateInit();

		//If this is a deep link direct to spaces / screens:

		this._createItems(spritesheet, 1);

		//The swap side (particle manager, swap-only sceneProps members, grid
		//guide container, reveal wiring) mounts into the slots. Fires
		//HomeWebGLDeps.reportSceneCreated (ex globals.preloadController
		//.homeParticlesCreated — async-only effects, so firing before the
		//pass-2 items instead of after is equivalent):
		this._webgl.create(this._slots, spritesheet, this._syncSizesSnapshot(), this._createItemGeometry());

		this._createItems(spritesheet, 2);
		this._created = true;

		this.resize();
		setTimeout(() => {
			this.resize();
		}, 20);

		this.homeIntroStart();
	}

	public homeIntroStart() {
		let introElements = this._getItemsById('introp');
		introElements.forEach((item) => {
			item.fadeIn(0, 0, 0);
		});

		//Swap half (no-op for 'particles' — its intro scene starts from the
		//onParticlesReady handler inside create()):
		this._webgl.introStart();
	}

	//The screens-scene TV overlays (HomeScreens) are swap-side: their init /
	//tick / intro-show / glow wiring live inside ParticlesWebGL, fed by the
	//same HomeItemGeometry handed over in create().
	private homeIntroShow() {
		let introElements = this._getItemsById('introp');
		let d = 0.5;
		introElements.forEach((item) => {
			item.fadeIn(d, 0.2 + Math.random() * 0.2, 0.5);
			d += 0.5;
		});
	}

	private _initScreensElements() {
		//Absence-tolerant (items can be webglTypes-filtered — the factory's
		//filter; _getItemsById warns on the dev harness): framesprite stays
		//null (ScreensDepartment guards it) and _screensTvSprites stays null
		//(TimelineCSSSync's metric getters guard it):
		let frames = this._getItemsById('screen-frames');
		if (frames[0]) {
			globals.framesprite = frames[0].sprite;
		}

		//Same item sprites the swap side's HomeScreens.init caches — from here
		//on the CSS sync computes its screens-TV metrics from these (null
		//before, which mirrors HomeScreens' pre-init behaviour):
		let topLeft = this._getItemsById('screensTVTopLeft');
		let topRight = this._getItemsById('screensTVTopRight');
		if (topLeft[0] && topRight[0]) {
			this._screensTvSprites = {
				topLeft: topLeft[0].sprite,
				topRight: topRight[0].sprite
			};
		}
	}

	private _checkHasParticlesCreateInit() {
		this._zones.update();
	}

	/**
	 * Swap-side scene is built and measured (HomeWebGLDeps.onReady) — the
	 * shared half of the old onParticlesReady continuation: wire the screens
	 * elements off the item registry and schedule the container reveal. The
	 * swap half (manager resize + intro scene start) lives in the WebGL layer.
	 */
	public onWebGLReady(): void {
		this._initScreensElements();

		setTimeout(() => {
			this._$homeContainer.visible = true;
			this._$homeContainer.alpha = 1;
		}, webglHome.revealDelaysMs.containerReveal);
	}

	/**
	 * The size/layout object handed to the WebGL layer on create()/resize() —
	 * the stable-identity _sizes, with the zones/zonePositions refs
	 * re-snapshotted from sceneProps at this exact point (TimelineZones
	 * REPLACES those objects each update, and the WebGL layer must keep seeing
	 * the same resize-time snapshot staleness the old per-call
	 * _buildWebGLSizes literal gave it). The scalar fields are already
	 * maintained in place by resize()/setVirtualWidth()/_setSizes().
	 */
	private _syncSizesSnapshot(): HomeWebGLSizes {
		this._sizes.zones = this._sceneProps.zones as HomeWebGLSizes['zones'];
		this._sizes.zonePositions = this._sceneProps.zonePositions;

		return this._sizes;
	}

	/** Read access to the big-item geometry / registries (HomeItemGeometry) */
	private _createItemGeometry(): HomeItemGeometry {
		const timeline = this;

		return {
			hitTests: ParticleHitTests,
			targets: ParticleTargets,
			get itemSprites(): ReadonlyArray<PIXI.Sprite> {
				return timeline._items.map((item: TimelineItem) => item.sprite);
			},
			getItems: (id: string) => this._getItemsById(id),
			getMaskRegionItems: (region: string) => this._getItemsByMaskRegion(region)
		};
	}

	/** Hide / Place elements out of view  */
	public setupForWork(department: DepartmentType): void {
		this._$homeContainer.visible = false;
		this._webgl.setupForWork(department);
	}

	public showFromWork(department: DepartmentType): void {
		this._webgl.showFromWork(department);
	}

	/** Set if the timeline is active or not */
	public setActive(active: boolean): void {
		this._active = active;
	}

	/** Update the progress based on the users X drag */
	public setProgress(ratio: number, overrideActiveFlag: boolean = false): void {
		this._progress = this._sceneProps.progress.ratio = ratio;

		if (!this._created) {
			return;
		}

		if (!overrideActiveFlag && !this._active) {
			return;
		}

		this._setContainerX();
	}

	/** Set the CORE UI refs for elements overlaid over the home screen: */
	public setUIRefs(
		$intro: HTMLElement,
		$screens: HTMLElement,
		$spaces: HTMLElement,
		$dragToExplore: HTMLElement,
		$systems: HTMLElement,
		$workContainerHTML: HTMLElement
	): void {
		this._domTransitions.setUIRefs($intro, $screens, $spaces, $systems, $workContainerHTML);

		this._cssSync.setUIRefs($intro, $screens, $spaces, $dragToExplore, $systems, $workContainerHTML);
	}

	/** Set virtual width (Virtual width is needed in drag logic, so managed in main home controller) */
	public setVirtualWidth(width: number, viewPortCount: number): void {
		this._sizes.virtualWidth = width;
		this._sizes.viewPortCount = viewPortCount;

		//(The former globals.virtualWidth/virtualHeight/virtualYRatio writes
		//died here — swap-side readers use sceneProps.sizes.container now.)

		this.resize(true);

		DelayHelper.setTimeout(() => {
			this.resize();
		}, 20);
	}

	/** Resize event */

	private _setSizes(): void {
		this._sceneProps.sizes.window.h = this._sceneProps.sizes.container.h = device.innerHeight;
		this._sceneProps.sizes.window.w = device.innerWidth;
		this._sceneProps.sizes.container.w = this._sizes.virtualWidth;
		this._sceneProps.sizes.displayScale = this._sceneProps.sizes.container.w / this._sceneProps.sizes.native.w;

		if (this._sceneProps.sizes.displayScale > 1.0) {
			this._sceneProps.sizes.displayScale = 1.0;
		}

		//Mirror onto the shared sizes object (same moment the sceneProps value
		//is computed; nothing external reads between here and end of resize):
		this._sizes.displayScale = this._sceneProps.sizes.displayScale;

		//Hacked previously: let positionScale = this._virtualWidth  / (1920 * 2.5);
	}

	/**
	 * Set the core scene props - these is passed down to all particles /
	 * particleBehaviours so they have all the key data.
	 *
	 * SHARED MEMBERS ONLY: mouse / progress / sizes / flags (zones /
	 * zonePositions are written per-update by TimelineZones). The swap-only
	 * members (lookups / layouts / targets / colours) are augmented onto this
	 * SAME object by the WebGL layer in create() — object identity never
	 * changes.
	 */
	private _createSceneProps(): void {
		this._sceneProps = {
			mouse: {
				cords: { x: 0, y: 0 },
				parallax: { x: 0, y: 0 },
				scene: { x: 0, y: 0 },
				isDown: false,
				isTouch: false,
				progress: { x: 0, y: 0 },
				particleContainersX: {
					screens: 0,
					spaces: 0,
					systems: 0,
					home: 0
				},
				particleContainersY: {
					screens: 0,
					spaces: 0,
					systems: 0,
					home: 0
				}
			},
			progress: {
				ratio: 0.5,
				velocity: 0,
				containerX: 0,
				edgeX: 0,
				home: 1,
				spaces: 0,
				screens: 0
			},
			sizes: {
				window: { w: device.innerWidth, h: device.innerHeight },
				container: { w: this._sizes.virtualWidth, h: device.innerHeight },
				displayScale: 1.0,
				native: { w: webglHome.nativeSize.w, h: webglHome.nativeSize.h }
			},
			flags: {
				transitioning: false
			}
		};
	}

	public resize(firstResize: boolean = false): void {
		this._cssSync.setWidths();

		//device is the single viewport lookup (refreshed first thing in
		//App._onResize, so it is already current for this cascade). The old
		//redundant window.cachedInnerWidth/Height re-writes died here:
		this._sizes.innerWidth = device.innerWidth;
		this._sizes.innerHeight = device.innerHeight;
		this._sizes.virtualHeight = device.innerHeight;

		this._setSizes();

		//Set the scale of the
		let scale = this._sizes.virtualWidth / webglHome.layoutScaleBase;
		if (scale > 1.0) {
			scale = 1.0;
		}

		//Position scale
		//Note this divided by 2.5 as the layouts are currently compressed in this way to make transition quicker
		//Value can used to help recaluclate positions and paths
		let positionScale = (this._sizes.positionScale = this._sizes.virtualWidth / (1920 * this._sizes.viewPortCount));

		//Loop through the items and pass the new values:
		this._items.forEach((item: TimelineItem) => {
			item.resize(
				this._sizes.virtualWidth,
				this._sizes.virtualHeight,
				scale,
				this._sizes.innerWidth,
				positionScale,
				this._sizes.viewPortCount
			);
		});

		//Reset the width of the tracked elements
		this._cssSync.refreshWidths();

		//Swap-side resize (no-op until the WebGL layer's create() has run —
		//mirrors the old `if (this._particleManager)` guard):
		this._webgl.resize(this._syncSizesSnapshot(), firstResize);
	}

	public tick(
		mousecords: any = null,
		mouseIsDown: boolean = null,
		velocity: number = null,
		instant: boolean = false,
		skipCheck: boolean = false
	): void {
		if (!this._created) {
			return;
		}

		this._velocity = this._sceneProps.progress.velocity = velocity;

		this._zones.update();

		this._timelineRegionManager.tick(
			mousecords,
			this._containerX,
			this._sizes.virtualWidth,
			this._sizes.virtualHeight,
			this._progress,
			this._velocity
		);

		let ratioYPos = mousecords.y / this._sizes.innerHeight - 0.5;
		let ratioXPos = mousecords.x / this._sizes.innerWidth - 0.5;
		let velocityRatio = this._velocity / 10;

		ParticleHitTests.clear();
		ParticleTargets.clear();

		for (let i = 0, n = this._items.length; i < n; i++) {
			this._items[i].tick(
				this._progress,
				this._containerX,
				ratioYPos,
				ratioXPos,
				mouseIsDown,
				velocityRatio,
				instant,
				mousecords
			);
		}

		ParticleHitTests.tick();

		//Swap-side frame: the per-frame sceneProps.mouse writes (ex _setMouse*,
		//sourced from the same MouseController values as before), the particle
		//manager tick and the icon/systems container fades. All the moved
		//writes/fades are read only by swap-side code, so running them here —
		//instead of split across the top and bottom of this method — is
		//order-equivalent:
		this._webgl.tick({
			progress: this._progress,
			velocity: this._velocity,
			velocityRatio: velocityRatio,
			containerX: this._containerX,
			edgeX: this._sceneProps.progress.edgeX,
			mouseCords: this._mouseController.getMouseCords(),
			mouseIsDown: this._mouseController.mouseIsDown(),
			mouseRatioX: ratioXPos,
			mouseRatioY: ratioYPos,
			instant: instant
		});

		//CSS/DOM element sync — -show classes/transforms, explore scramble, projects-click prompt:
		this._cssSync.tick(instant);
	}

	/** Remove any animations */
	public resetIntervalsAndAnimations(): void {
		//Loop through the items:
		this._items.forEach((item: TimelineItem) => {
			item.resetIntervalsAndAnimations();
		});

		//Swap half (no-op for 'particles' — no swap-side intervals today):
		this._webgl.resetIntervalsAndAnimations();
	}

	/** Reset for deeplink */
	public resetForDeeplink(): void {
		//Item/DOM half (see TimelineDOMTransitions):
		this._domTransitions.resetForDeeplink();

		//Swap half (no-op for 'particles' — the deeplink reset is item/DOM only):
		this._webgl.deeplink(null);
	}

	/** Reset when coming back from work */
	public resetFromWork(department: DepartmentType): void {
		//Item/DOM half (see TimelineDOMTransitions) — returns the shared offY:
		let offY: number = this._domTransitions.resetFromWork(department);

		//Swap half (no-op for 'particles' — the back-from-work reset is
		//item/DOM only; the field scenes restore via showFromWork):
		this._webgl.resetFromWork(department, offY);
	}

	public resetAnimationFreeze(): void {
	}

	public homeIntroHover(): void {
		this._webgl.introHover();
	}

	public setButtonHover(center: Point): void {
		this._webgl.onExploreHover(center);
	}

	public setButtonOut(type: DepartmentType): void {
		globals.buttonActive = false;

		//(A legacy _setItemState('HomeParticles', ...) call was removed here:
		//no item with that id exists in the home item config (HomeConfig), so it matched nothing —
		//the old home-particles field is webgl-side now.)
		this._webgl.onExploreOut(type);
	}

	public homeIntroMouseOut(): void {
		this._webgl.introOut();
	}

	/**
	 * Entry point for the work-intro transition frames (WorkController ->
	 * HomeController -> here); the whole body is swap-side.
	 */
	public updateWorkTransitionParticles(introXPx: number, introProgressRatio: number) {
		this._webgl.updateWorkTransitionParticles(introXPx, introProgressRatio);
	}

	//Private
	//-----------------------------------------------------------------------------------------------
	//-----------------------------------------------------------------------------------------------
	//-----------------------------------------------------------------------------------------------
	//-----------------------------------------------------------------------------------------------

	//Create the larger items used in the home structure like logos, swirls, etc
	//Item construction is factory-side (verbatim move — see TimelineItemFactory);
	//the returned batch lands in the shared _items registry. Region creation is
	//threshold-machine wiring, so it stays here (same position as before —
	//after the pass-1 items, before the containerX/resize refresh):
	private _createItems(spritesheet: PIXI.Spritesheet, pass = 1): void {
		this._itemFactory.createItems(spritesheet, pass).forEach((item: TimelineItem) => {
			this._items.push(item);
		});

		if (pass == 1) {
			//Loop through and create all regions:
			this._config.regions.forEach((region: TimelineRegionParams) => {
				this._createTimelineRegion(region);
			});
		}

		this._setContainerX();
		this.resize();
	}

	/** Set the container x Postion */
	private _setContainerX(): void {
		let totalSlides = webglHome.totalSlides;
		let scale = 1 - this._sizes.viewPortCount / totalSlides;
		let viewPortFix = (this._sizes.innerWidth / 2) * scale;
		let progressFix = (this._progress / 2) * scale;

		this._containerX =
			-1 * ((this._progress + progressFix) * (this._sizes.virtualWidth - this._sizes.innerWidth) - viewPortFix);

		this._sceneProps.progress.containerX = this._containerX;
		this._sceneProps.progress.edgeX =
			this._sceneProps.sizes.container.w - this._sceneProps.sizes.window.w + this._containerX;
	}

	/** Timeline region effects */
	private _createTimelineRegion(params: TimelineRegionParams): void {
		params.pixiContainer = this._$homeContainer;
		params.onEnter = (id: string) => this._onTimeRegionEnter(id);
		params.onExit = (id: string) => this._onTimeRegionExit(id);
		this._timelineRegionManager.add(params);
	}


	public introComplete(): void {
		this._state = TimelineStateType.HOME;

		DelayHelper.setTimeout(() => this._dom.removeClass(document.querySelector('.home__intro'), '-hideatstart'), 400);

		//Spaces overflow elements:

		//Swap half — the intro-complete particle handoff (field introComplete,
		//HOME state, main scene start), plus the screens-TV intro reveal and
		//glow wiring (HomeScreens.showAtStart / .glows, swap-side). The shared
		//item fades below don't feed any of it, so the single grouped call is
		//order-equivalent to the pre-seam split:
		this._webgl.introComplete();
		this.homeIntroShow();

		//Absence-tolerant (`?.` — items can be webglTypes-filtered; the empty
		//lookup warns on the dev harness via _getItemsById):
		this._getItemsById('spacebgarea')[0]?.fadeIn(2, 0);

		this._getItemsById('systems-grid-screens1')[0]?.fadeIn(2, 0.2);
		this._getItemsById('systems-grid-screens2')[0]?.fadeIn(2, 0.16);
		this._getItemsById('systems-grid-screens3')[0]?.fadeIn(2, 0.25);

		this._getItemsById('introp1')[0]?.fadeIn(2, 0.15);
		this._getItemsById('introp2')[0]?.fadeIn(2, 0.35);
		this._getItemsById('introp3')[0]?.fadeIn(2, 0.15);

		//Screens overflow elements:


		this._sceneProps.masks = {
		};

		gsap.TweenMax.to('.temp_footer', 0.4, { autoAlpha: 1, delay: 3.5 });
	}

	/** HomeWebGLDeps.colouriseSystemsItems entry point (body in TimelineSceneColours). */
	public colouriseSystemsScene(colourSet, instant) {
		this._sceneColours.colouriseSystemsScene(colourSet, instant);
	}

	/** HomeWebGLDeps.colouriseSpacesItems entry point (body in TimelineSceneColours). */
	public colouriseSpacesScene(colourSet, screenColour: number | string = null, floorColour = null) {
		this._sceneColours.colouriseSpacesScene(colourSet, screenColour, floorColour);
	}


	private _showRegion(id: string) {
		if (id === 'Home') {
			this._state = TimelineStateType.HOME;
			this._webgl.onRegionEnter(id);

			this._mouseController.lastPointerWasTouch() ? 'Swipe to explore' : 'Drag to explore';
		}

		if (id === 'Screens') {
			this._deps.setPagingIndex(1);
			this._state = TimelineStateType.SCREENS;
			this._mouseController.setHiddenState(false);
			this._cssSync.showProjectsClickUI();
		}
	}

	private _onTimeRegionEnter(id: string): void {

		//The timeline keeps its own state guard; the swap half (icon hover)
		//routes through the contract:
		if (id === 'HomeLogoArea' && this._state === TimelineStateType.HOME) {
			this._webgl.onRegionEnter(id);
		}

		if (id === 'SpacesBoxArea') {
			this._sceneProps.flags.boxHover = true;
		}

		if (id === 'Screens' || id === 'Home') {
			this._showRegion(id);
			return;
		}

		if (id === 'Spaces') {
			this._deps.setPagingIndex(2);
			this._state = TimelineStateType.SPACES;
			this._webgl.onRegionEnter(id);
			this._deps.hideDragPrompt();
			this._cssSync.showProjectsClickUI('spaces');
		}

		if (id === 'Systems') {
			this._deps.setPagingIndex(3);
		}
	}

	private _onTimeRegionExit(id: string): void {
		if (this._state === TimelineStateType.INTRO) {
			return;
		}

		//Swap half (no-op for 'particles' — exits only clear shared flags):
		this._webgl.onRegionExit(id);

		if (id === 'SpacesBoxArea') {
			this._sceneProps.flags.boxHover = false;
		}
	}

	private _getItemsById(id: string): Array<TimelineItem> {
		let items: Array<TimelineItem> = [];

		this._items.forEach((timelineItem: TimelineItem) => {
			if (timelineItem.id === id) {
				if (
					timelineItem.type === TimelineItemType.PARTICLE_FIELD ||
					timelineItem.type === TimelineItemType.RELATIVE_CHILDREN
				) {
					let groupItems = (timelineItem as TimelineItemGroup).getItems();
					for (let i = 0; i < groupItems.length; i++) {
						items.push(groupItems[i]);
					}
				}

				if (timelineItem.type === TimelineItemType.PARTICLE) {
					items.push(timelineItem);
				}
			}
		});

		//THE central empty-lookup tell for the /dev/home/ harness: every by-id
		//path (this class, TimelineSceneColours deps, HomeItemGeometry.getItems)
		//funnels through here, and consumers must tolerate a missing id — a
		//webglTypes-filtered item (TimelineItemFactory) is the usual cause:
		if (items.length === 0 && dev.active) {
			console.warn(`[dev] no items for id ${id} — filtered by webglTypes?`);
		}

		return items;
	}

	/**
	 * The registry filter behind HomeItemGeometry.getMaskRegionItems: items
	 * tagged `maskRegion: '<region>'` in the home item config (mirrors _getItemsById,
	 * keyed on TimelineItem.maskRegion instead of id).
	 */
	private _getItemsByMaskRegion(region: string): Array<TimelineItem> {
		let items: Array<TimelineItem> = [];

		this._items.forEach((timelineItem: TimelineItem) => {
			if (timelineItem.maskRegion === region) {
				if (
					timelineItem.type === TimelineItemType.PARTICLE_FIELD ||
					timelineItem.type === TimelineItemType.RELATIVE_CHILDREN
				) {
					let groupItems = (timelineItem as TimelineItemGroup).getItems();
					for (let i = 0; i < groupItems.length; i++) {
						items.push(groupItems[i]);
					}
				}

				if (timelineItem.type === TimelineItemType.PARTICLE) {
					items.push(timelineItem);
				}
			}
		});

		//Empty-lookup tell, consistent with _getItemsById — consumers must
		//tolerate an untagged/filtered region (a missing maskRegion tag or a
		//webglTypes-filtered tagged item is the usual cause):
		if (items.length === 0 && dev.active) {
			console.warn(`[dev] no items for mask region ${region} — untagged in the home item config or filtered by webglTypes?`);
		}

		return items;
	}
}
